import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";

/**
 * Qaytarib bo'lmaydigan amal uchun tasdiqlash oynasi.
 *
 * `window.confirm` o'rniga: brauzer oynasi mavzuni (tungi rejim) va tilni
 * bilmaydi, hamda so'rov ketayotganda "kutilmoqda" holatini ko'rsata olmaydi —
 * ega tugmani ikki marta bosib yuborardi.
 *
 * `onConfirm` xato tashlasa oyna OCHIQ qoladi va tugmalar yana faollashadi:
 * xabarni chaqiruvchi (toast) ko'rsatadi, lekin foydalanuvchi qayta urinishi
 * uchun kontekst yo'qolmaydi.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => Promise<void> | void;
}) {
  const { t } = useTranslation("common");
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Xabar chaqiruvchida ko'rsatiladi — oyna ochiq qoladi.
    } finally {
      setBusy(false);
    }
  }

  return (
    // Amal ketayotganda oyna yopilmaydi (Escape/fon bosilishi ham).
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      contentClassName="max-w-sm"
    >
      <div className="space-y-4 p-5">
        <div className="flex items-start gap-3">
          {destructive && (
            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive [&_svg]:size-4">
              <AlertTriangle />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
              {title}
            </h2>
            {description && (
              <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-none"
          >
            {cancelLabel ?? t("actions.cancel")}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={run}
            disabled={busy}
            className="flex-1"
          >
            {busy ? t("actions.processing") : (confirmLabel ?? t("actions.confirm"))}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
